import { Bird, Shield, Check, Phone, Star, MapPin } from "lucide-react";
import UnifiedCalculator from "../components/UnifiedCalculator";
import GutterCleanGallery from "../components/GutterCleanGallery";
import { Logo, Header } from "../components/Layout";
import { Button } from "../components/ui/button";

const suburbs = ["Chermside", "Aspley", "Albany Creek", "Stafford", "Kedron", "Nundah", "Wavell Heights", "Geebung", "Zillmere", "Bridgeman Downs"];

export default function BirdProofingNorthsidePage() {
  return (
    <>
      <>
        <title>Solar Panel Bird Proofing Northside Brisbane | Pest Free Ipswich & Brisbane</title>
        <meta name="description" content="Professional solar panel bird proofing across Brisbane's Northside. Serving Chermside, Aspley, Albany Creek & more. Get an instant quote — 10-year warranty." />
      </>

      <Header />
      <div className="min-h-screen bg-slate-50">
        {/* Hero */}
        <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-[#5BC0DE]/20 text-white pt-28 pb-16 md:pt-32 md:pb-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#5BC0DE]/20 border border-[#5BC0DE]/30 rounded-full px-4 py-1.5 mb-5">
                  <MapPin className="w-4 h-4 text-[#5BC0DE]" />
                  <span className="text-sm font-medium text-[#5BC0DE]">Northside Brisbane</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
                  Solar Panel Bird Proofing <span className="text-[#5BC0DE]">Northside Brisbane</span>
                </h1>
                <p className="text-lg text-slate-300 mb-6">
                  Pigeons nesting under your solar panels? We remove nests, clean up the mess, and install professional-grade mesh backed by a 10-year warranty. Serving all Northside suburbs.
                </p>
                <div className="flex flex-wrap gap-3 mb-6">
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />10-Year Warranty</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />Same Week Service</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />QBCC Licensed</div>
                </div>
                <div className="flex items-center gap-3">
                  <a href="tel:0737081422" className="flex items-center gap-2 bg-[#7ABC32] hover:bg-[#6AAD28] text-white font-semibold px-6 py-3 rounded-full transition-all">
                    <Phone className="w-5 h-5" /> (07) 3708 1422
                  </a>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                    <span className="text-sm text-slate-300 ml-1">4.9 on Google</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                <Logo className="h-32 md:h-40 opacity-90" />
              </div>
            </div>
          </div>
        </section>

        {/* Calculator Section */}
        <section className="py-12 md:py-16" id="calculator">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800">Get Your Instant Price</h2>
              <p className="text-slate-600 mt-2">Bird proofing + optional pest control & termite inspection — one combined quote</p>
            </div>
            <UnifiedCalculator source="northside_brisbane" />
          </div>
        </section>

        {/* Before/After Gallery */}
        <GutterCleanGallery />

        {/* Why Birds Are a Problem */}
        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">Why Northside Brisbane Homes Need Bird Proofing</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: "Bird Mites", desc: "Parasites that migrate from nests into your home, causing itchy bites and allergic reactions.", color: "#E74C3C" },
                { title: "Health Risks", desc: "Bird droppings carry diseases and allergens that can affect your family's health.", color: "#E74C3C" },
                { title: "Risk Losing Efficiency", desc: "Panel damage, built up debris, scratches and stains on panels reduce solar output.", color: "#D2691E" },
                { title: "Fire Hazard", desc: "Dry nesting materials are perfect tinder — a serious risk under electrical equipment.", color: "#D2691E" },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-4 rounded-lg bg-slate-50">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: item.color + '15' }}>
                    <Shield className="w-5 h-5" style={{ color: item.color }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">{item.title}</h3>
                    <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Suburbs Served */}
        <section className="py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Suburbs We Service on Brisbane's Northside</h2>
            <div className="flex flex-wrap justify-center gap-2">
              {suburbs.map(s => (
                <span key={s} className="bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700">{s}</span>
              ))}
            </div>
            <p className="text-slate-500 text-sm mt-4">And all surrounding Northside suburbs</p>
          </div>
        </section>

        {/* Photos/videos placeholder - ready for when user provides them */}

        {/* CTA */}
        <section className="py-12 bg-gradient-to-r from-[#5BC0DE] to-[#3498aa] text-white text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Get Rid of the Birds?</h2>
            <p className="text-lg text-white/80 mb-6">Get your instant quote above or call us now for same-week service.</p>
            <a href="tel:0737081422" className="inline-flex items-center gap-2 bg-white text-[#5BC0DE] font-bold px-8 py-3 rounded-full hover:bg-slate-100 transition-all">
              <Phone className="w-5 h-5" /> Call (07) 3708 1422
            </a>
          </div>
        </section>
      </div>
    </>
  );
}
